import { Controller } from "@hotwired/stimulus";

const MAX_CONNECTIONS = 30;
const MAX_CONDITIONS_PER_CONNECTION = 10;

export default class RoleConnectionsController extends Controller {
  static targets = ["connectionList", "connection", "json"];
  static values = {
    mainRoleLabel: String,
    rolePlaceholder: String,
    logicAnd: String,
    logicOr: String,
    removeConnection: String,
    addCondition: String,
    condModeHas: String,
    condModeLacks: String,
    removeCondition: String,
  };

  connect() {
    this.#serialize();
  }

  addConnection() {
    if (this.connectionTargets.length >= MAX_CONNECTIONS) return;

    const div = document.createElement("div");
    div.className = "border border-base-300 rounded-lg p-4 mb-4";
    div.dataset.roleConnectionsTarget = "connection";
    div.innerHTML = this.#connectionTemplate();
    this.connectionListTarget.appendChild(div);
    this.#serialize();
  }

  removeConnection(event) {
    event.currentTarget.closest("[data-role-connections-target='connection']")?.remove();
    this.#serialize();
  }

  addCondition(event) {
    const connection = event.currentTarget.closest("[data-role-connections-target='connection']");
    const list = connection.querySelector(".js-condition-list");
    if (list.querySelectorAll(".js-condition-row").length >= MAX_CONDITIONS_PER_CONNECTION) return;

    const row = document.createElement("div");
    row.className = "flex items-center gap-2 mb-2 js-condition-row";
    row.innerHTML = this.#conditionTemplate();
    list.appendChild(row);
    this.#serialize();
  }

  removeCondition(event) {
    event.currentTarget.closest(".js-condition-row")?.remove();
    this.#serialize();
  }

  serialize(event) {
    if (event?.type === "submit") this.#serialize();
  }

  onChange() {
    this.#serialize();
  }

  #serialize() {
    const connections = this.connectionTargets
      .map((conn) => ({
        main_role_id: conn.querySelector("[data-field='main_role_id']")?.value?.trim() || "",
        logic: conn.querySelector("[data-field='logic']")?.value || "and",
        conditions: [...conn.querySelectorAll(".js-condition-row")]
          .map((row) => ({
            mode: row.querySelector("[data-field='mode']")?.value || "has",
            role_id: row.querySelector("[data-field='role_id']")?.value?.trim() || "",
          }))
          .filter((c) => c.role_id),
      }))
      .filter((c) => c.main_role_id);

    if (this.hasJsonTarget) {
      this.jsonTarget.value = JSON.stringify(connections);
    }
  }

  #connectionTemplate() {
    return `
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-sm font-semibold">${this.mainRoleLabelValue || "Main role:"}</span>
          <input type="text" class="input input-bordered input-xs w-36 font-mono"
                 placeholder="${this.rolePlaceholderValue || "Role ID"}" data-field="main_role_id"
                 data-action="input->role-connections#onChange">
          <select class="select select-bordered select-xs" data-field="logic"
                  data-action="change->role-connections#onChange">
            <option value="and">${this.logicAndValue || "AND (all must match)"}</option>
            <option value="or">${this.logicOrValue || "OR (any must match)"}</option>
          </select>
        </div>
        <button type="button"
                class="btn btn-ghost btn-sm btn-square text-error/70 hover:text-error"
                aria-label="${this.removeConnectionValue || "Remove connection"}"
                data-action="click->role-connections#removeConnection">
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>
      <div class="js-condition-list mb-2"></div>
      <button type="button" class="btn btn-xs btn-outline btn-neutral"
              data-action="click->role-connections#addCondition">
        <i class="fa-solid fa-plus mr-1"></i>${this.addConditionValue || "Add Condition"}
      </button>
    `;
  }

  #conditionTemplate() {
    return `
      <select class="select select-bordered select-xs w-28" data-field="mode"
              data-action="change->role-connections#onChange">
        <option value="has">${this.condModeHasValue || "has role"}</option>
        <option value="lacks">${this.condModeLacksValue || "lacks role"}</option>
      </select>
      <input type="text" class="input input-bordered input-xs w-36 font-mono"
             placeholder="${this.rolePlaceholderValue || "Role ID"}" data-field="role_id"
             data-action="input->role-connections#onChange">
      <button type="button"
              class="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error"
              aria-label="${this.removeConditionValue || "Remove condition"}"
              data-action="click->role-connections#removeCondition">
        <i class="fa-solid fa-trash-can"></i>
      </button>
    `;
  }
}
